// Números e listas da tela de início de cada área (HubWorldHome), na versão
// "ação primeiro" de 12/09/2026: o destaque conta o que está vivo e os blocos
// carregam uma dica curta. Só dado que já existe em API ou store barata:
// fila (conversationStats e a lista carregada), funil (stage_stats e reports
// do Kanban), tarefas, agenda do dia, campanhas, fluxos, caixas e CSAT (só
// para administrador). Valor ausente vira "·", nunca zero falso.
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import KanbanAPI from 'dashboard/api/kanban';
import FunnelAPI from 'dashboard/api/funnel';
import CampaignsAPI from 'dashboard/api/campaigns';
import CSATReportsAPI from 'dashboard/api/csatReports';
import LeadAppointmentsAPI from 'dashboard/api/leadAppointments';
import conversationWorkflowsAPI from 'dashboard/api/conversationWorkflows';
import {
  endOfDay,
  startOfDay,
} from 'dashboard/routes/dashboard/kanban/agenda/agendaHelpers';
import {
  waitingDurationFromMinutes,
  waitingMinutesFromEpoch,
} from 'dashboard/helper/waitingLabel';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { toIntlLocale } from 'shared/helpers/intlLocale';

import { ATTENTION_HEALTH } from 'dashboard/helper/workflowHealth';
const FLOW_ORDER = {
  failed: 0,
  unconfirmed: 0,
  delivery_attention: 0,
  ok: 1,
  idle: 2,
  never_ran: 2,
  draft: 3,
  inactive: 4,
};
const EMPTY = '·';
const MAX_ROWS = 5;
const MAX_INBOX_CHIPS = 5;
const DAY_SECONDS = 86_400;

// Quando a campanha sai: o JSON manda segundos (one_off) e a lista antiga
// mandava ISO. Valor inválido vira null, nunca 1970.
export const campaignScheduledAt = campaign => {
  const raw = campaign?.scheduled_at;
  if (!raw) return null;
  let date;
  if (typeof raw === 'number') {
    date = new Date(raw > 1e12 ? raw : raw * 1000);
  } else {
    date = new Date(raw);
  }
  return Number.isFinite(date.getTime()) ? date : null;
};

export const isCampaignActive = campaign => {
  if (!campaign || campaign.campaign_status === 'completed') return false;
  if (campaign.is_drip) return campaign.drip_status === 'running';
  if (campaign.campaign_type === 'ongoing') return campaign.enabled === true;
  return (
    campaign.campaign_status === 'active' ||
    campaign.campaign_status === 'processing'
  );
};

export const isCampaignScheduled = campaign => {
  if (!campaign || campaign.campaign_status === 'completed') return false;
  if (campaign.campaign_status === 'scheduled') return true;
  const at = campaignScheduledAt(campaign);
  return Boolean(at && at > new Date());
};

export const campaignState = campaign => {
  if (isCampaignActive(campaign)) return 'active';
  if (isCampaignScheduled(campaign)) return 'scheduled';
  if (campaign?.campaign_status === 'completed') return 'done';
  return 'paused';
};

// Para onde a linha da campanha leva: a lista do canal dela.
export const campaignDestinationId = campaign => {
  if (isCampaignScheduled(campaign)) return 'campaigns_scheduled';
  const type = campaign?.inbox?.channel_type || '';
  const medium = campaign?.inbox?.medium || '';
  if (type === 'Channel::WebWidget') return 'campaigns_livechat';
  if (type === 'Channel::Sms') return 'campaigns_sms';
  if (type === 'Channel::TwilioSms' && medium !== 'whatsapp') {
    return 'campaigns_sms';
  }
  return 'campaigns_whatsapp';
};

const CAMPAIGN_TONE = { active: 'green', scheduled: 'blue', done: 'gray' };
const FLOW_TONE = {
  ok: 'green',
  failed: 'amber',
  unconfirmed: 'amber',
  delivery_attention: 'amber',
};

const count = value => (value === null || value === undefined ? EMPTY : value);
// 401/403 é "não liberado para o seu perfil", não lista vazia: sem essa
// distinção o atendente via "Nenhum fluxo" e "0 campanhas" que não existem.
const isForbidden = error => [401, 403].includes(error?.response?.status);
const toEpoch = value => {
  if (!value) return null;
  if (typeof value === 'number') return value > 1e12 ? value / 1000 : value;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.getTime() / 1000 : null;
};

export function useHubWorldStats(worldId) {
  const { t, locale } = useI18n();
  const store = useStore();
  const conversationStats = useMapGetter('conversationStats/getStats');
  const allConversations = useMapGetter('getAllConversations');
  const inboxes = useMapGetter('inboxes/getInboxes');
  const currentRole = useMapGetter('getCurrentRole');

  const funnel = ref({
    name: null,
    stages: [],
    items: null,
    value: null,
    error: false,
  });
  const csatError = ref(false);
  // SiteUP CRM (relatorios r1, itens 4 e 23): erro de carregamento vira estado
  // de erro POR INDICADOR, nunca zero na tela. `error: true` faz o hero mostrar
  // o marcador de vazio em vez de um numero inventado.
  const reports = ref({ revenue: null, won: null, rate: null, error: false });
  const agenda = ref({ count: null, next: null });
  const campaigns = ref([]);
  const campaignsLoaded = ref(false);
  const flows = ref([]);
  const flowsLoaded = ref(false);
  const campaignsForbidden = ref(false);
  const flowsForbidden = ref(false);
  const csat = ref(null);

  const intl = toIntlLocale(locale.value);
  const money = new Intl.NumberFormat(intl, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
  const shortTime = new Intl.DateTimeFormat(intl, {
    hour: '2-digit',
    minute: '2-digit',
  });
  const shortDate = new Intl.DateTimeFormat(intl, {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

  // ---- fila -----------------------------------------------------------
  const mineCount = computed(() => conversationStats.value?.mineCount || 0);
  const unassignedCount = computed(
    () => conversationStats.value?.unAssignedCount || 0
  );
  const allCount = computed(() => conversationStats.value?.allCount || 0);
  const longestWaiting = computed(() => {
    const waiting = (allConversations.value || []).filter(
      chat => Number(chat.waiting_since) > 0
    );
    if (!waiting.length) return null;
    const chat = waiting.reduce((oldest, current) =>
      Number(current.waiting_since) < Number(oldest.waiting_since)
        ? current
        : oldest
    );
    return {
      minutes: Math.max(0, waitingMinutesFromEpoch(chat.waiting_since)),
      name: chat.meta?.sender?.name || '',
    };
  });
  const isAdministrator = computed(() => currentRole.value === 'administrator');

  // ---- tarefas e agenda -----------------------------------------------
  const tasksToday = computed(() => store.getters['tasks/getTodayCount'] ?? 0);
  const tasksOverdue = computed(
    () => store.getters['tasks/getOverdueCount'] ?? 0
  );

  // ---- campanhas e fluxos ---------------------------------------------
  const activeCampaigns = computed(
    () => campaigns.value.filter(isCampaignActive).length
  );
  const scheduledCampaigns = computed(() =>
    campaigns.value.filter(isCampaignScheduled)
  );
  const nextCampaign = computed(() => {
    const dates = scheduledCampaigns.value
      .map(campaignScheduledAt)
      .filter(Boolean)
      .sort((a, b) => a - b);
    return dates[0] || null;
  });
  const activeFlows = computed(() => flows.value.filter(flow => flow.active));
  const attentionFlows = computed(() =>
    flows.value.filter(flow => ATTENTION_HEALTH.has(flow.health))
  );
  const lastFlowRun = computed(() => {
    const stamps = flows.value
      .map(flow => Number(flow.last_execution_at) || 0)
      .filter(Boolean);
    return stamps.length ? Math.max(...stamps) : null;
  });

  // ---- carregadores ---------------------------------------------------
  const loadQueue = () =>
    store
      .dispatch('conversationStats/get', {
        assigneeType: 'me',
        status: 'active',
      })
      .catch(() => {});

  const loadCsat = async () => {
    if (!isAdministrator.value) return;
    try {
      const to = Math.floor(Date.now() / 1000);
      const { data } = await CSATReportsAPI.getMetrics({
        from: to - 30 * DAY_SECONDS,
        to,
      });
      const total = Number(data?.total_count) || 0;
      if (!total) return;
      const sum = Object.entries(data?.ratings_count || {}).reduce(
        (acc, [rating, amount]) => acc + Number(rating) * Number(amount),
        0
      );
      csat.value = { average: sum / total, total };
      csatError.value = false;
    } catch {
      // Erro nao vira zero nem media inventada: o KPI diz que falhou.
      csat.value = null;
      csatError.value = true;
    }
  };

  const loadFunnel = async () => {
    try {
      await store.dispatch('funnel/fetch');
    } catch {
      // sem funil, o destaque diz isso
    }
    const list = store.getters['funnel/getFunnels'] || [];
    const selected = store.getters['funnel/getSelectedFunnel'] || list[0];
    if (!selected?.id) {
      funnel.value = {
        name: null,
        stages: [],
        items: 0,
        value: 0,
        error: false,
      };
      return;
    }
    try {
      const { data } = await FunnelAPI.getStageStats(selected.id);
      const stats = data?.stages || {};
      // Ordem do funil (position); etapa sem posição fica no fim, na ordem
      // em que o stats a devolveu.
      const stages = Object.entries(stats)
        .map(([key, stage], index) => ({
          key,
          name: stage?.name || selected.stages?.[key]?.name || key,
          position: selected.stages?.[key]?.position ?? 1000 + index,
          count: Number(stage?.count) || 0,
          value: Number(stage?.total_value) || 0,
        }))
        .sort((a, b) => Number(a.position) - Number(b.position));
      funnel.value = {
        name: selected.name,
        stages,
        items: stages.reduce((acc, stage) => acc + stage.count, 0),
        value: stages.reduce((acc, stage) => acc + stage.value, 0),
        error: false,
      };
    } catch {
      funnel.value = {
        name: selected.name,
        stages: [],
        items: null,
        value: null,
        error: true,
      };
    }
  };

  // SiteUP CRM (relatorios r1, item 4): a "Receita" do mundo Vender pedia o
  // relatorio SEM periodo e lia a receita por criacao, que e recorte
  // por CRIACAO do cartao. Em producao, 4 dos 6 ganhos dos ultimos 30 dias sao
  // de cartoes criados antes da janela: a venda de hoje de um lead antigo nao
  // entrava. Agora pede 30 dias explicitos e le o bloco closedInPeriod, que e
  // recortado pela DATA DE FECHAMENTO.
  const loadReports = async () => {
    try {
      const to = Math.floor(Date.now() / 1000);
      const { data } = await KanbanAPI.getReports({
        from: to - 30 * DAY_SECONDS,
        to,
      });
      const closed = data?.closedInPeriod || {};
      reports.value = {
        revenue: Number(closed.revenue) || 0,
        won: Number(closed.won) || 0,
        rate: Number(closed.conversionRate) || 0,
        error: false,
      };
    } catch {
      reports.value = { revenue: null, won: null, rate: null, error: true };
    }
  };

  const loadAgenda = async () => {
    try {
      const now = new Date();
      const { data } = await LeadAppointmentsAPI.list({
        from: startOfDay(now).toISOString(),
        to: endOfDay(now).toISOString(),
      });
      const alive = (Array.isArray(data) ? data : []).filter(
        item => item.status !== 'cancelled'
      );
      const upcoming = alive
        .map(item => new Date(item.scheduled_at))
        .filter(d => Number.isFinite(d.getTime()) && d >= now)
        .sort((a, b) => a - b);
      agenda.value = {
        count: alive.length,
        next: upcoming.length ? shortTime.format(upcoming[0]) : null,
      };
    } catch {
      agenda.value = { count: null, next: null };
    }
  };

  const loadCampaigns = async () => {
    try {
      const { data } = await CampaignsAPI.get();
      campaigns.value = Array.isArray(data) ? data : [];
      campaignsForbidden.value = false;
    } catch (error) {
      campaigns.value = [];
      campaignsForbidden.value = isForbidden(error);
    }
    campaignsLoaded.value = true;
  };

  const loadFlows = async () => {
    try {
      const { data } = await conversationWorkflowsAPI.get();
      flows.value = data?.payload ?? [];
      flowsForbidden.value = false;
    } catch (error) {
      flows.value = [];
      flowsForbidden.value = isForbidden(error);
    }
    flowsLoaded.value = true;
  };

  const loaders = {
    attendance: () =>
      Promise.all([
        loadQueue(),
        loadCsat(),
        store.dispatch('inboxes/get').catch(() => {}),
      ]),
    sales: () =>
      Promise.all([
        store.dispatch('tasks/fetchNotifications').catch(() => {}),
        loadFunnel(),
        loadReports(),
        loadAgenda(),
      ]),
    marketing: loadCampaigns,
    automation: loadFlows,
  };

  // ---- destaque -------------------------------------------------------
  const kpi = (id, label, value) => ({ id, label, value });

  const queueHero = () => {
    const parts = [
      t('HUB.HOME.QUEUE_UNASSIGNED', unassignedCount.value, {
        count: unassignedCount.value,
      }),
      t('HUB.HOME.QUEUE_MINE', mineCount.value, { count: mineCount.value }),
    ];
    const wait = longestWaiting.value;
    if (wait) {
      parts.push(
        t('HUB.HOME.QUEUE_WAIT', {
          wait: waitingDurationFromMinutes(wait.minutes, t),
          name: wait.name,
        })
      );
    }
    const kpis = [
      kpi('mine', t('HUB.HOME.KPI_MINE'), mineCount.value),
      kpi('unassigned', t('HUB.STATS.UNASSIGNED'), unassignedCount.value),
    ];
    if (csatError.value) {
      kpis.push(kpi('csat', t('HUB.HOME.KPI_ERROR'), EMPTY));
    } else if (csat.value) {
      kpis.push(
        kpi(
          'csat',
          t('HUB.HOME.KPI_CSAT'),
          csat.value.average.toLocaleString(intl, { maximumFractionDigits: 1 })
        )
      );
    } else if (wait) {
      kpis.push(
        kpi(
          'wait',
          t('HUB.STATS.MAX_WAIT'),
          waitingDurationFromMinutes(wait.minutes, t)
        )
      );
    }
    return {
      title: t('HUB.HOME.QUEUE_TITLE', allCount.value, {
        count: allCount.value,
      }),
      subtitle: parts.join(' · '),
      kpis,
    };
  };

  const funnelHero = () => {
    const { name, stages, items, value, error } = funnel.value;
    if (error) {
      // Nao dizer "carregando" pra sempre quando a chamada falhou.
      return { title: t('HUB.HOME.FUNNEL_ERROR'), subtitle: '', kpis: [] };
    }
    if (items === null) {
      return { title: t('HUB.HOME.FUNNEL_LOADING'), subtitle: '', kpis: [] };
    }
    if (!name) {
      return {
        title: t('HUB.HOME.FUNNEL_NONE'),
        subtitle: t('HUB.HOME.FUNNEL_NONE_SUB'),
        kpis: [],
      };
    }
    const busy = stages.filter(stage => stage.count > 0);
    const subtitle = busy.length
      ? busy.map(stage => `${stage.name} ${stage.count}`).join(' · ')
      : t('HUB.HOME.FUNNEL_EMPTY', { name });
    const title = [
      t('HUB.HOME.DEALS_OPEN', items, { count: items }),
      value ? money.format(value) : null,
    ]
      .filter(Boolean)
      .join(' · ');
    return {
      title,
      subtitle,
      kpis: [
        kpi(
          'revenue',
          reports.value.error
            ? t('HUB.HOME.KPI_ERROR')
            : t('HUB.HOME.KPI_REVENUE'),
          reports.value.revenue === null
            ? EMPTY
            : money.format(reports.value.revenue)
        ),
        kpi(
          'won',
          reports.value.error ? t('HUB.HOME.KPI_ERROR') : t('HUB.HOME.KPI_WON'),
          count(reports.value.won)
        ),
        kpi(
          'rate',
          reports.value.error
            ? t('HUB.HOME.KPI_ERROR')
            : t('HUB.HOME.KPI_WIN_RATE'),
          reports.value.rate === null ? EMPTY : `${reports.value.rate}%`
        ),
      ],
    };
  };

  const flowsHero = () => {
    if (flowsForbidden.value) {
      return { title: t('HUB.HOME.NOT_FOR_ROLE'), subtitle: '', kpis: [] };
    }
    const active = activeFlows.value.length;
    const attention = attentionFlows.value;
    let subtitle;
    if (!flowsLoaded.value) {
      subtitle = '';
    } else if (attention.length) {
      // Nome junto do plural: vue-i18n só interpola outros campos na forma
      // t(chave, nomeados, plural).
      subtitle = t(
        'HUB.HOME.FLOWS_ATTENTION',
        { count: attention.length, name: attention[0].name },
        attention.length
      );
    } else if (lastFlowRun.value) {
      subtitle = t('HUB.HOME.FLOWS_HEALTHY', {
        time: dynamicTime(lastFlowRun.value),
      });
    } else {
      subtitle = t('HUB.HOME.FLOWS_NONE_SUB');
    }
    return {
      title: t('HUB.HOME.FLOWS_TITLE', active, { count: active }),
      subtitle,
      kpis: [
        kpi('attention', t('HUB.STATS.FLOWS_ATTENTION'), attention.length),
        kpi('total', t('HUB.STATS.FLOWS_TOTAL'), flows.value.length),
        kpi(
          'last_run',
          t('HUB.HOME.KPI_LAST_RUN'),
          lastFlowRun.value ? dynamicTime(lastFlowRun.value) : EMPTY
        ),
      ],
    };
  };

  const newActionHero = () => ({
    title: t('HUB.HOME.NEW_ACTION_TITLE'),
    subtitle: t('HUB.HOME.NEW_ACTION_SUB'),
    kpis: [],
  });

  const heroes = {
    attendance: queueHero,
    sales: funnelHero,
    marketing: newActionHero,
    automation: flowsHero,
  };

  const hero = computed(
    () => heroes[worldId.value]?.() || { title: '', subtitle: '', kpis: [] }
  );

  // ---- listas vivas ---------------------------------------------------
  const campaignRows = () => {
    const order = { active: 0, scheduled: 1, paused: 2, done: 3 };
    return [...campaigns.value]
      .sort((a, b) => order[campaignState(a)] - order[campaignState(b)])
      .slice(0, MAX_ROWS)
      .map(campaign => {
        const state = campaignState(campaign);
        const at = campaignScheduledAt(campaign);
        const updated = toEpoch(campaign.updated_at);
        let time = '';
        if (state === 'scheduled' && at) time = shortDate.format(at);
        else if (updated) time = dynamicTime(updated);
        return {
          id: `campaign-${campaign.id}`,
          title: campaign.title || t('HUB.HOME.UNTITLED'),
          meta: [campaign.inbox?.name, campaign.audience_description]
            .filter(Boolean)
            .join(' · '),
          badge: {
            label: t(`HUB.HOME.CAMPAIGN_STATE.${state}`),
            tone: CAMPAIGN_TONE[state] || 'gray',
          },
          time,
          destinationId: campaignDestinationId(campaign),
        };
      });
  };

  const flowRows = () =>
    [...flows.value]
      .sort((a, b) => {
        const byHealth =
          (FLOW_ORDER[a.health] ?? 9) - (FLOW_ORDER[b.health] ?? 9);
        if (byHealth) return byHealth;
        return (
          (Number(b.last_execution_at) || 0) -
          (Number(a.last_execution_at) || 0)
        );
      })
      .slice(0, MAX_ROWS)
      .map(flow => {
        const channels = (flow.channels || []).map(channel =>
          t(`FLOW_AUTOMATIONS.INDEX.CHANNEL.${channel}`)
        );
        const lastRun = flow.last_execution_at
          ? t('FLOW_AUTOMATIONS.INDEX.HEALTH.LAST_RUN', {
              time: dynamicTime(Number(flow.last_execution_at)),
            })
          : t('FLOW_AUTOMATIONS.INDEX.HEALTH.NEVER');
        return {
          id: `flow-${flow.id}`,
          title: flow.name,
          meta: [...channels, lastRun].join(' · '),
          badge: {
            label: t(`FLOW_AUTOMATIONS.INDEX.HEALTH.${flow.health || 'ok'}`),
            tone: FLOW_TONE[flow.health] || 'gray',
          },
          time: '',
          routeName: 'conversation_workflows_edit',
          params: { workflowId: flow.id },
        };
      });

  const rows = computed(() => {
    if (worldId.value === 'marketing') return campaignRows();
    if (worldId.value === 'automation') return flowRows();
    return [];
  });

  const listLoaded = computed(() => {
    if (worldId.value === 'marketing') return campaignsLoaded.value;
    if (worldId.value === 'automation') return flowsLoaded.value;
    return true;
  });

  const listForbidden = computed(() => {
    if (worldId.value === 'marketing') return campaignsForbidden.value;
    if (worldId.value === 'automation') return flowsForbidden.value;
    return false;
  });

  const listSummary = computed(() => {
    if (
      worldId.value === 'marketing' &&
      campaignsLoaded.value &&
      !campaignsForbidden.value
    ) {
      return [
        t('HUB.LIVE.ACTIVE_CAMPAIGNS', activeCampaigns.value, {
          count: activeCampaigns.value,
        }),
        t('HUB.HOME.SCHEDULED_COUNT', scheduledCampaigns.value.length, {
          count: scheduledCampaigns.value.length,
        }),
      ].join(' · ');
    }
    return '';
  });

  // ---- dicas dos blocos -----------------------------------------------
  const hints = computed(() => {
    const overdue = tasksOverdue.value
      ? t('HUB.STATS.TASKS_OVERDUE', tasksOverdue.value, {
          count: tasksOverdue.value,
        })
      : t('HUB.STATS.NONE_OVERDUE');
    const nextAgenda = agenda.value.next
      ? t('HUB.STATS.AGENDA_NEXT', { when: agenda.value.next })
      : t('HUB.STATS.NO_EVENTS');
    const scheduled = scheduledCampaigns.value.length;
    const nextScheduled = nextCampaign.value
      ? t('HUB.HOME.NEXT_AT', { when: shortDate.format(nextCampaign.value) })
      : '';
    return {
      tasks: `${t('HUB.LIVE.TASKS_TODAY', tasksToday.value, {
        count: tasksToday.value,
      })} · ${overdue}`,
      agenda:
        agenda.value.count === null
          ? EMPTY
          : `${t('HUB.AGENDA_CARD.TODAY_COUNT', {
              count: agenda.value.count,
            })} · ${nextAgenda}`,
      campaigns_scheduled:
        campaignsLoaded.value && !campaignsForbidden.value
          ? [
              t('HUB.HOME.SCHEDULED_COUNT', scheduled, { count: scheduled }),
              nextScheduled,
            ]
              .filter(Boolean)
              .join(' · ')
          : EMPTY,
    };
  });

  const badges = computed(() => ({
    tasks: tasksOverdue.value
      ? { value: tasksOverdue.value, tone: 'amber' }
      : null,
  }));

  const chipCounts = computed(() => ({
    mine: mineCount.value,
    unassigned: unassignedCount.value,
  }));

  // Um chip por caixa, para abrir a fila só daquele canal.
  const inboxChips = computed(() =>
    (inboxes.value || []).slice(0, MAX_INBOX_CHIPS).map(inbox => ({
      id: `inbox-${inbox.id}`,
      label: inbox.name,
      icon: getInboxIconByType(inbox.channel_type, inbox.medium, 'line'),
      routeName: 'inbox_dashboard',
      params: { inbox_id: inbox.id },
    }))
  );

  const load = () => loaders[worldId.value]?.();
  onMounted(load);
  watch(worldId, load);

  return {
    hero,
    rows,
    listLoaded,
    listForbidden,
    listSummary,
    hints,
    badges,
    chipCounts,
    inboxChips,
    mineCount,
  };
}
